import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getAtBatState } from '../store/selectors/getAtBat';
import { useSelector } from 'react-redux';
import { PitchByPitchDesktopWrapper, TabsWrapper, ContentWrapper, FlexWrapper, LiveFeedWrapper, InfoDivider, ScoreProbabilityWrapper } from '../atbat.style';
import AtBatVideo from './AtBatVideo';
import Tabs from '@gameday/components/Tabs/Tabs';
import AtBatPitchFxDataContainer from '../containers/AtBatPitchFxDataContainer';
import LiveFeedPlays from '@gameday/components/LiveFeed/liveFeed/LiveFeedPlays';
import useTracking from '@gameday/utils/hooks/useTracking';
import GamedayFieldVisionPlayer from '@gameday/components/FieldVision/components/GamedayFieldVisionPlayer';
import { FvodApp } from '@gameday/components/FieldVision/fieldvision.style';
import FieldVisionLogin from './FieldVisionLogin';
import useNativeLoginFunc from '@gameday/utils/hooks/useNativeLoginFunc';

import {
    Description,
    Event,
    Matchup,
    Probability,
    Statcast,
    DetailSituation
} from '../summary';

const atBatPitchFxConfig = {
    batterScaling: true,
    tiltShift: false,
    base_width: 1158,
    base_height: 869,
    fixed_width: 960,
    hoverData: false,
    fontSize: 92,
    clip: {
        width: 220,
        height: 270
    },
    mode: '2d'
};

const DesktopAtBatPlay = ({
    playIndex,
    onAuthOpen,
    setTutorialOpen
}) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [tabkeyId, setTabkeyId] = useState('atbat_tabs');
    const [isOptedIn, setIsOptedIn] = useState(false);
    const Tracking = useTracking();
    const nativeLoginFunc = useNativeLoginFunc();

    const {
        playData = {},
        isLoading,
        deviceInteraction,
        gamePk,
        items,
        teams,
        venueId,
        language,
        accessToken,
        isLoggedIn,
        fieldVisionODAvailable,
        forcedFieldVisionOn
    } = useSelector(getAtBatState(playIndex));

    const controllerRef = useRef();
    const { highlights, resultPlayGuid } = playData;

    const handleTabChange = useCallback((e) => {
        const selectedIndex = e?.currentTarget.dataset.index * 1;
        setSelectedIndex(e?.currentTarget.dataset.index * 1);
        const cfg = {
            componentName: ['AtBat', 'Play Details'],
            selectedIndex,
            text: e?.currentTarget?.innerText || '',
            activity: 'Tab Click'
        };

        Tracking.track.track_click(cfg);
    }, [playIndex]);

    const tabbedView = useMemo(() => {
        const tabName = items[selectedIndex]?.type;
        switch (tabName) {
            case 'pitchbypitch':
                return <FlexWrapper>
                    <PitchByPitchDesktopWrapper><AtBatPitchFxDataContainer playIndex={playIndex} pitchFxConfig={ atBatPitchFxConfig}/></PitchByPitchDesktopWrapper>
                    <LiveFeedWrapper>
                        <LiveFeedPlays
                            playArr={[playIndex]}
                            showAllPitches={true}
                            showPitchesOnly={true}
                            showBasesText={false}
                        />
                    </LiveFeedWrapper>
                </FlexWrapper >;
            case 'video':
                return highlights ? <AtBatVideo gamePk={gamePk} highlights={highlights} isLoading={isLoading} deviceInteraction={deviceInteraction} /> : null;
            case 'gameday3d':
                return ((fieldVisionODAvailable || forcedFieldVisionOn) && isLoggedIn && isOptedIn)
                    ? <FlexWrapper><FvodApp>
                        <GamedayFieldVisionPlayer
                            ref={controllerRef}
                            gamePk={gamePk * 1}
                            debug={false}
                            accessToken={accessToken}
                            loop={true}
                            playId={resultPlayGuid}
                            embeddedControls={'fvod'}
                            defaultCam={'batter'}
                            autoPlay={false}
                            setTutorialOpen={setTutorialOpen}
                            key={gamePk + accessToken}
                        />
                    </FvodApp></FlexWrapper>
                    : <FlexWrapper><FieldVisionLogin venueId={venueId} isLoggedIn={isLoggedIn} setIsOptedIn={setIsOptedIn} onAuthOpen={nativeLoginFunc || onAuthOpen} /></FlexWrapper>;
        }
    }, [selectedIndex, items]);

    const tabsControl = useMemo(() => {
        return <Tabs activeTab={selectedIndex} items={items} handleTabChange={handleTabChange} key={`atbat-tabs-${tabkeyId}`}/>;
    }, [selectedIndex, items]);

    const {
        about: {
            halfInning = '',
            inning,
            isTopInning,
            isScoringPlay
        } = {},
        matchup = {},
        playMetrics: {
            preCount = {},
            playEvents: metricsPlayEvents = [],
            awayTeamWinProbability,
            awayTeamWinProbabilityAdded,
            homeTeamWinProbability,
            homeTeamWinProbabilityAdded
        } = {},
        result: {
            event = '',
            description = '',
            awayScore,
            homeScore
        } = {},
        playEvents = []
    } = playData;

    const {
        hitData = {}
    } = playEvents?.[playEvents?.length - 1] || {};

    const {
        away = {},
        home = {}
    } = teams;

    useEffect(() => {
        setTabkeyId(`atbat-tabs-${items?.length}`);
    }, [items]);

    const validLaunchAngle = (typeof hitData?.launchAngle !== 'undefined') && (hitData?.launchAngle !== null);
    return (
        <>
            <DetailSituation
                inning={inning}
                halfInning={halfInning}
                awayScore={awayScore}
                homeScore={homeScore}
                away={away}
                home={home}
                isTopInning={isTopInning}
                isScoringPlay={isScoringPlay}
            />
            <Event
                event={event}
            />
            <Description
                description={description}
            />

            <ScoreProbabilityWrapper>
                <Probability
                    halfInning={halfInning}
                    away={away}
                    home={home}
                    awayTeamWinProbability={awayTeamWinProbability}
                    awayTeamWinProbabilityAdded={awayTeamWinProbabilityAdded}
                    homeTeamWinProbability={homeTeamWinProbability}
                    homeTeamWinProbabilityAdded={homeTeamWinProbabilityAdded}
                />
            </ScoreProbabilityWrapper>

            <InfoDivider />

            {validLaunchAngle
                ? <>
                    <Statcast
                        lastPlayEvent={{
                            hitData
                        }}
                    />
                    <InfoDivider />
                </>
                : null}

            <Matchup
                away={away}
                home={home}
                matchup={matchup}
                language={language}
                deviceInteraction={deviceInteraction}
                offense={metricsPlayEvents?.length && metricsPlayEvents[metricsPlayEvents.length - 1]?.offense}
                count={preCount}
            />

            {items.length > 1
                ? <>
                    <InfoDivider />
                    <TabsWrapper>{tabsControl}</TabsWrapper>
                </>
                : null}

            <InfoDivider />

            <ContentWrapper >{tabbedView}</ContentWrapper>
        </>
    );
};

DesktopAtBatPlay.propTypes = {
    playIndex: PropTypes.number.isRequired,
    onAuthOpen: PropTypes.func.isRequired,
    setTutorialOpen: PropTypes.func
};

export default DesktopAtBatPlay;
